/**
 * Company search behind the header and hero search boxes.
 *
 * Results are built from feeds the site already loads rather than a search
 * request per keystroke: the GMP board (every issue currently in the market,
 * mainboard and SME), the DRHP tracker (filings on their way there) and the
 * catalog list (everything published). Each company is matched once and points
 * at the richest page that exists for it.
 *
 * When a server-side search endpoint ships, only buildSearchIndex has to
 * change -- rankSearchResults and the search box work off the same row shape.
 */
import { deriveBoard } from './ipoCatalogAdapters';
import { toGmpSlug } from './gmpDataMapper';
import { isSameCompany, matchKey } from './smeIpos';
import { splitFilingName } from './drhpTracker';

const STATUS_LABEL = {
  open: 'Open now',
  closing_today: 'Closing today',
  upcoming: 'Opening soon',
  closed: 'Bidding closed',
  allotted: 'Allotted',
  listed: 'Listed',
};

// Live issues first, then filings, then the back catalog
const SOURCE_RANK = { market: 0, tracker: 1, catalog: 2 };

/**
 * One searchable row per company.
 *
 * @param {object} feeds
 * @param {object[]} feeds.gmpRows - selectGmpBoardRows (mainboard + SME)
 * @param {object[]} feeds.trackerRecords - GET /api/core/ipos/drhp-tracker
 * @param {object[]} feeds.catalogRecords - GET /api/core/ipos list items
 */
export const buildSearchIndex = ({ gmpRows = [], trackerRecords = [], catalogRecords = [] } = {}) => {
  const byCompany = new Map();
  const add = (entry) => {
    const key = matchKey(entry.company);
    if (!key || !entry.company || byCompany.has(key)) return;
    // The feeds abbreviate differently -- "Adroit Industries" in the market
    // feed is "Adroit Industries (India) Limited" in the catalog -- so an
    // exact key is not enough to tell two companies apart
    for (const existing of byCompany.values()) {
      if (isSameCompany(existing.company, entry.company)) return;
    }
    // The first source to claim a company wins, and they are added richest first
    byCompany.set(key, { ...entry, key });
  };

  gmpRows.forEach((row) =>
    add({
      company: row.name,
      board: row.board === 'SME' ? 'sme' : 'mainboard',
      source: 'market',
      detail: STATUS_LABEL[row.status] || 'In the market',
      to: `/ipo-gmp/${row.slug || toGmpSlug(row.name)}`,
    })
  );

  trackerRecords.forEach((record) =>
    add({
      company: splitFilingName(record.company_name).company,
      board: ['sme', 'mainboard'].includes(record.board) ? record.board : null,
      source: 'tracker',
      detail: record.status === 'approved' ? 'RHP filed' : 'DRHP filed',
      to: `/drhp-filings/${record.id}`,
    })
  );

  catalogRecords.forEach((record) =>
    add({
      company: record.company_name,
      board: deriveBoard(record.ipo_type, record.listing_exchange),
      source: 'catalog',
      detail: 'Offer document',
      to: `/ipo/${record.id}`,
    })
  );

  return [...byCompany.values()];
};

// "Anand Seamless Limited" -> ["anand", "seamless", "limited"]
const wordsOf = (company) =>
  String(company || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

/**
 * How well a company matches, or null for no match. Lower scores rank higher.
 *
 * A name that starts with what was typed beats one where it appears in the
 * middle, so "core" puts "Core Integra" above "Multicore Systems".
 */
export const scoreMatch = (company, needle) => {
  const name = String(company || '').toLowerCase();
  if (!needle) return null;
  if (name === needle) return 0;
  if (name.startsWith(needle)) return 1;
  if (wordsOf(company).some((word) => word.startsWith(needle))) return 2;
  return name.includes(needle) ? 3 : null;
};

/**
 * @param {object[]} index - buildSearchIndex output
 * @param {string} query
 * @param {number} limit
 */
export const rankSearchResults = (index = [], query = '', limit = 7) => {
  const needle = String(query || '').trim().toLowerCase();
  if (needle.length < 2) return [];

  return index
    .map((entry) => ({ entry, score: scoreMatch(entry.company, needle) }))
    .filter(({ score }) => score !== null)
    .sort(
      (a, b) =>
        a.score - b.score ||
        SOURCE_RANK[a.entry.source] - SOURCE_RANK[b.entry.source] ||
        a.entry.company.length - b.entry.company.length ||
        a.entry.company.localeCompare(b.entry.company)
    )
    .slice(0, limit)
    .map(({ entry }) => entry);
};
